import {
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CreditCardOutlined,
    ExclamationCircleFilled,
    FileExcelOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {
    Badge,
    Button,
    Card,
    Col,
    ConfigProvider,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import frFR from "antd/locale/fr_FR";
import * as XLSX from "xlsx";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import RecuCurrentFormat from "../production/components/docs/RecuCurrentFormat";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import { rightsService } from "../../services/rightsService";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER, PARAMETERS } from "../../variables/constants";
import { frenchDate, frenchDateSimple } from "../../variables/functions";
import { getParametre } from "../../hooks/useHooks";
import { displayNumbers } from "../../variables/numbersFunctions";
import dayjs from "dayjs";

const { confirm } = Modal;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;

const STATUS_COLORS = {
    regle:    { bg: "#f6ffed", border: "#b7eb8f" },
    nonRegle: { bg: "#fffbe6", border: "#ffe58f" },
};

// v2 : la liste ne contient QUE des non-effectués (la fonction SQL filtre déjà
// reglements = 0) → on retire le filtre « Réglé/Non réglé » devenu inutile et on
// clarifie le titre. Aucun changement du flux d'encaissement.
// Composant V2 AUTONOME (séparé de la v1) : comportement v2 figé.
// Pour inclure les affaires nouvelles validées sans règlement, ne pas appliquer de filtre
// de période par défaut ici.
const EnAttenteDEncaissementV2 = () => {
    const v2 = true;
    const [data, setData]                               = useState([]);
    const [loadingContrats, setLoadingContrats]         = useState(true);
    const [pagination, setPagination]                   = useState({ current: 1, pageSize: 10 });
    const [selecting, setSelecting]                     = useState(false);
    const [modalModePaiement, setModalModePaiement]     = useState(false);
    const [contractsToPay, setContractsToPay]           = useState([]);
    const [selectedRows, setSelectedRows]               = useState([]);
    const [selectedRowKeys, setSelectedRowKeys]         = useState([]);
    const [filteredInfo, setFilteredInfo]               = useState({});
    const [sortedInfo, setSortedInfo]                   = useState({});
    const [total, setTotal]                             = useState(0);
    const [membres, setMembres]                         = useState([]);
    const [selectedCompagnies, setSelectedCompagnies]   = useState([]);
    const [banques, setBanques]                         = useState([]);
    const [count, setCount]                             = useState({});
    // V2 must include validated new business without a règlement line, like the old page.
    // Do not apply a default month filter so the list is not artificially narrowed.
    const [selectedDate, setSelectedDate]               = useState(null);
    const [modePaiement, setModePaiement]               = useState("ESPECES");
    const [numeroPaiement, setNumeroPaiement]           = useState("");
    const [selectedContrat, setSelectedContrat]         = useState(null);
    const [currentPage, setCurrentPage]                 = useState(1);
    const [pageSize, setPageSize]                       = useState(10);
    const [banque, setBanque]                           = useState("");
    const [dateVirement, setDateVirement]               = useState(null);
    const [reference, setReference]                     = useState("");
    const [searchText, setSearchText]                   = useState("");
    // Observation / commentaire d'encaissement : choix prédéfinis (paramètre OBSERVATION_ENCAISSEMENT)
    // + texte libre. `observation` = valeur envoyée ; `observationSelected` = sélection du Select.
    const [observationChoices, setObservationChoices]   = useState([]);
    const [observationSelected, setObservationSelected] = useState(undefined);
    const [observation, setObservation]                 = useState("");
    const [newObservation, setNewObservation]           = useState("");
    const [modePaiementForm]                            = Form.useForm();
    // Encaissement PARTIEL (HLA093) : montant réellement encaissé ; défaut = prime totale (= plein).
    // Un partiel fait quitter le contrat de CETTE liste (1 règlement créé) ; le reste se suit dans /encaissements.
    const canEncaissementPartiel                        = rightsService("HLA093");
    const [montantEncaisse, setMontantEncaisse]         = useState(null);

    const navigate = useNavigate();

    /* ── Données triées : NON REGLE d'abord ── */
    const sortedData = [...data].sort((a, b) => {
        const aRegle = a.regle === "REGLE" ? 1 : 0;
        const bRegle = b.regle === "REGLE" ? 1 : 0;
        return aRegle - bRegle;
    });

    const filteredData = sortedData.filter((r) => {
        if (!searchText) return true;
        const q = searchText.toLowerCase();
        return (
            (r.souscripteur || "").toLowerCase().includes(q) ||
            (r.numeroPolice || "").toLowerCase().includes(q)
        );
    });

    /* ── Fetch ── */
    const fetchData = async (params = {}) => {
        setLoadingContrats(true);
        try {
            const response = await axiosInstance.get("/api/reglement/en-attente-v2", {
                params: {
                    page: (params.pagination?.current || 1) - 1,
                    size: params.pagination?.pageSize || 10,
                    search: searchText || "",
                    ...params.filters,
                    compagnie: params.filters?.compagnie?.join(","),
                },
            });
            const { content, totalElements } = response.data;
            const ctrs = content.map((contrat, key) => ({
                key: contrat.key || `${contrat.sourceType}:${contrat.codeId}`,
                codeId: contrat.contratCodeId || contrat.codeId,
                reglementCodeId: contrat.reglementCodeId,
                sourceType: contrat.sourceType,
                id: key,
                numeroPolice: contrat.numeroPolice || contrat.police,
                souscripteur: contrat?.prenomSouscripteur
                    ? `${contrat.prenomSouscripteur} ${contrat.nomSouscripteur}`
                    : contrat?.nomSouscripteur || "",
                organisme: contrat?.organismeCode,
                dateEffet: contrat?.dateEffet ? frenchDate(contrat.dateEffet) : "",
                dateEcheance: contrat?.dateEcheance ? frenchDateSimple(contrat.dateEcheance) : "",
                regle: "NON REGLE",
                avenant: contrat.avenant || "AN",
                primeTotale: displayNumbers(contrat?.montant),
                primeNette: displayNumbers(contrat?.montant),
                accessoires: displayNumbers(0),
                rawPrimeTotale: contrat?.montant || 0,
                data: contrat,
            }));
            setData(ctrs);
            setPagination({ ...params.pagination, total: totalElements });
            setTotal(totalElements);
        } catch (error) {
            console.error("Failed to fetch data:", error);
        } finally {
            setLoadingContrats(false);
        }
    };

    useEffect(() => {
        const filters = {
            ...filteredInfo,
            ...(selectedDate ? { startDate: selectedDate[0], endDate: selectedDate[1] } : {}),
            compagnie: selectedCompagnies,
        };
        fetchData({ pagination: { current: currentPage, pageSize }, filters });

        axiosInstance.get(`/api/parametre/type/BANQUE`).then((res) => setBanques(res.data)).catch(console.log);
        // Choix d'observation d'encaissement (paramètre éditable OBSERVATION_ENCAISSEMENT).
        getParametre(PARAMETERS.observationEncaissement)
            .then((res) => setObservationChoices(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setObservationChoices([]));

        axiosInstance.get(`/api/organisme/assurance`).then((res) => {
            const membs = res.data.map((m, i) => ({ key: i, text: m.raisonSociale, value: m.code }));
            [
                { key: "DTRF", text: "DTRF", value: "DTRF" },
                { key: "DTRF-Lome", text: "DTRF-LOME", value: "LOME" },
                { key: "DTRF-Kpalime", text: "DTRF-KPALIME", value: "KPALIME" },
                { key: "DTRF-Atakpame", text: "DTRF-ATAKPAME", value: "ATAKPAME" },
                { key: "DTRF-Sokode", text: "DTRF-SOKODE", value: "SOKODE" },
                { key: "DTRF-Kara", text: "DTRF-KARA", value: "KARA" },
                { key: "DTRF-Dapaong", text: "DTRF-DAPAONG", value: "DAPAONG" },
                { key: "STCA", text: "STCA", value: "STCA" },
            ].forEach((x) => membs.push(x));
            setMembres(membs);
        }).catch(console.log);

        axiosInstance.get(`/api/contrat/count`).then((res) => setCount(res.data)).catch(console.log);
    }, [currentPage, pageSize, selectedDate]);

    /* ── Export Excel ── */
    const exportToExcel = () => {
        const rows = filteredData.map((r, i) => ({
            "N°": i + 1,
            "N° Police": r.numeroPolice || "",
            "Souscripteur": r.souscripteur || "",
            "Date Effet": r.dateEffet || "",
            "Date Échéance": r.dateEcheance || "",
            "Prime Totale (FCFA)": r.rawPrimeTotale,
            "Statut": r.regle,
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        ws["!cols"] = [
            { wch: 5 }, { wch: 18 }, { wch: 28 }, { wch: 16 }, { wch: 16 }, { wch: 18 }, { wch: 14 },
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Attente encaissement");
        XLSX.writeFile(wb, `attente_encaissement_${dayjs().format("YYYY-MM-DD")}.xlsx`);
    };

    /* ── Colonnes ── */
    const columns = [
        {
            title: "#",
            key: "index",
            width: 50,
            render: (_, __, i) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {(currentPage - 1) * pageSize + i + 1}
                </Text>
            ),
        },
        {
            title: "Statut",
            dataIndex: "regle",
            key: "regle",
            width: 130,
            render: (_, record) =>
                record.regle === "REGLE" ? (
                    <Tag icon={<CheckCircleOutlined />} color="success" style={{ borderRadius: 6 }}>
                        Réglé
                    </Tag>
                ) : (
                    <Tag icon={<ClockCircleOutlined />} color="warning" style={{ borderRadius: 6 }}>
                        Non réglé
                    </Tag>
                ),
            // v2 : pas de filtre (toutes les lignes sont « Non réglé »).
            ...(v2 ? {} : {
                filters: [
                    { text: "Réglé", value: "REGLE" },
                    { text: "Non réglé", value: "NON REGLE" },
                ],
                filteredValue: filteredInfo.regle || null,
                onFilter: (value, record) => record.regle === value,
            }),
        },
        {
            title: "Souscripteur",
            key: "souscripteur",
            render: (_, record) => (
                <div>
                    <Text strong style={{ fontSize: 13 }}>{record.souscripteur}</Text>
                    <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>{record.numeroPolice}</Text>
                    </div>
                </div>
            ),
        },
        // {
        //     title: "Origine",
        //     key: "sourceType",
        //     width: 150,
        //     render: (_, record) => (
        //         <Tag color={record.sourceType === "REGLEMENT_NON_ENCAISSE" ? "orange" : "blue"} style={{ borderRadius: 4 }}>
        //             {record.sourceType === "REGLEMENT_NON_ENCAISSE" ? "Règlement créé" : "Sans règlement"}
        //         </Tag>
        //     ),
        // },
        {
            title: "Date effet",
            dataIndex: "dateEffet",
            key: "dateEffet",
            width: 120,
            render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text>,
        },
        {
            title: "Date échéance",
            dataIndex: "dateEcheance",
            key: "dateEcheance",
            width: 130,
            render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text>,
        },
        {
            title: "Prime totale",
            dataIndex: "primeTotale",
            key: "primeTotale",
            width: 140,
            render: (_, record) => (
                <Text strong style={{ fontSize: 13, color: record.regle === "REGLE" ? "#389e0d" : "#d48806" }}>
                    {record.primeTotale} <span style={{ fontWeight: 400, fontSize: 11 }}>FCFA</span>
                </Text>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_, record) => (
                <Space size={4}>
                    {rightsService("HLA037") && record.numeroPolice && record.regle !== "REGLE" && (
                        <Tooltip title="Encaisser">
                            <Button
                                type="primary"
                                size="small"
                                icon={<CreditCardOutlined />}
                                onClick={() => { setSelectedContrat(record); setMontantEncaisse(record.rawPrimeTotale); setObservation(""); setObservationSelected(undefined); setNewObservation(""); setModalModePaiement(true); }}
                                style={{ borderRadius: 6, background: "#52c41a", borderColor: "#52c41a" }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="Voir le contrat">
                        <Button
                            size="small"
                            icon={<SearchOutlined />}
                            onClick={() => navigate(`/details-contrat/${record.codeId}`)}
                            style={{ borderRadius: 6 }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    // ── Reçu d'encaissement : imprimable JUSTE APRÈS le succès de l'encaissement ──
    const printRecu = useRef(null);
    const handleRecu = useReactToPrint({ content: () => printRecu.current, documentTitle: "Recu_encaissement" });
    const [recuOpen, setRecuOpen] = useState(false);
    const [recuContrat, setRecuContrat] = useState(null);
    const [recuReglement, setRecuReglement] = useState(null);
    const [loadingRecu, setLoadingRecu] = useState(false);

    // Charge les entités COMPLÈTES (comme Encaissements.openRecu) : le contrat par codeId puis le
    // règlement créé retrouvé par police — le DTO de liste est allégé (reçu faux sinon).
    const ouvrirRecuApresEncaissement = (target, createdReglement) => {
        const initial = createdReglement || target;
        setRecuContrat(target?.contrat || null);
        setRecuReglement(initial);
        setRecuOpen(true);
        const contratCodeId = target?.contratCodeId || target?.codeId;
        if (!contratCodeId) return;
        setLoadingRecu(true);
        axiosInstance.get(`/api/contrat/codeId/${contratCodeId}`)
            .then((res) => {
                const fullContrat = res.data;
                setRecuContrat(fullContrat);
                const police = fullContrat?.numeroPoliceP || target?.numeroPolice;
                if (!police) { setLoadingRecu(false); return; }
                axiosInstance.get(`/api/reglement/police/${police}`)
                    .then((resp) => {
                        const list = Array.isArray(resp.data) ? resp.data : [];
                        const full = (createdReglement?.codeId && list.find((r) => r.codeId === createdReglement.codeId))
                            || list.find((r) => r.codeId === initial?.codeId)
                            || initial;
                        setRecuReglement(full || initial);
                        setLoadingRecu(false);
                    })
                    .catch(() => setLoadingRecu(false));
            })
            .catch(() => setLoadingRecu(false));
    };

    // Fermer le reçu → recharger la liste (le contrat encaissé disparaît, compteurs à jour).
    const fermerRecuEtRafraichir = () => { setRecuOpen(false); window.location.reload(); };

    /* ── Confirmation & règlement ── */
    const alertReglement = (contractToPay) => {
        confirm({
            title: "Confirmer l'encaissement",
            icon: <ExclamationCircleFilled />,
            content: (
                <div>
                    <p>Encaisser le contrat <strong>{contractToPay?.numeroPolice}</strong> ?</p>
                    <p style={{ color: "#888", fontSize: 12 }}>Toutes vos actions sont historisées.</p>
                </div>
            ),
            okText: "Confirmer",
            okType: "primary",
            cancelText: "Annuler",
            onOk() { reglementContrat(contractToPay); },
        });
    };

    const alertReglements = (cts) => {
        confirm({
            title: `Confirmer l'encaissement de ${cts.length} contrat(s) ?`,
            icon: <ExclamationCircleFilled />,
            content: "Toutes vos actions sont historisées.",
            okText: "Confirmer",
            okType: "primary",
            cancelText: "Annuler",
            onOk() { reglementsContrats(cts); },
        });
    };

    const executeReglementContrat = (cont) => {
        const target = cont || selectedContrat;
        // Encaissement PARTIEL (HLA093) : si le montant saisi est < prime, on transmet montantPaye.
        // Sinon (plein, ou sans le droit) → paramètre absent, comportement inchangé.
        const prime = target?.rawPrimeTotale;
        const partiel =
            !(selecting && contractsToPay.length > 0) &&
            canEncaissementPartiel &&
            montantEncaisse != null &&
            prime != null &&
            montantEncaisse < prime;
        // Query string commune aux 3 appels : montantPaye (encaissement partiel HLA093),
        // dateVirement (VIREMENT : date de valeur → date de règlement), observation (commentaire).
        const parts = [];
        if (partiel) parts.push(`montantPaye=${montantEncaisse}`);
        if (modePaiement === "VIREMENT" && dateVirement) {
            parts.push(`dateVirement=${encodeURIComponent(dayjs(dateVirement).toISOString())}`);
        }
        if (observation && observation.trim()) {
            parts.push(`observation=${encodeURIComponent(observation.trim())}`);
        }
        const qs = parts.length ? `?${parts.join("&")}` : "";
        const safe = (v, fallback = "-") => encodeURIComponent(v || fallback);
        if (target?.sourceType === "REGLEMENT_NON_ENCAISSE") {
            const numero = safe(numeroPaiement || reference || "P");
            const ref = safe(banque || reference || "ESPECES");
            return axiosInstance.get(`/api/reglement/${target.codeId}/${target.reglementCodeId}/${LOGGED_USER.codeId}/${safe(modePaiement, "ESPECES")}/${numero}/${ref}/${prime}/${safe(target.avenant || "AN")}${qs}`);
        }
        if (modePaiement === "ESPECES") {
            return axiosInstance.get(`/api/reglement/especes/contrat/codeId/${target.codeId}/${LOGGED_USER.codeId}${qs}`);
        }
        return axiosInstance.get(`/api/reglement/contrat/codeId/${target.codeId}/${LOGGED_USER.codeId}/${modePaiement}/${numeroPaiement || reference}/${banque || reference}${qs}`);
    };

    // Ajout à la volée d'un choix d'observation (persiste en Paramètres OBSERVATION_ENCAISSEMENT)
    // puis le sélectionne. La liste reste éditable depuis l'écran Paramètres existant.
    const addObservationChoice = () => {
        const lib = (newObservation || "").trim();
        if (!lib) return;
        const exists = observationChoices.some(
            (o) => (o.libelleParametre || "").toLowerCase() === lib.toLowerCase()
        );
        if (exists) {
            setObservationSelected(lib);
            setObservation(lib);
            setNewObservation("");
            return;
        }
        axiosInstance.post(
            "/api/parametre",
            JSON.stringify({ typeParametre: PARAMETERS.observationEncaissement, libelleParametre: lib }),
            { headers: { "Content-Type": "application/json" } }
        )
            .then((res) => {
                setObservationChoices((prev) => [...prev, res?.data || { libelleParametre: lib }]);
                setObservationSelected(lib);
                setObservation(lib);
                setNewObservation("");
                toast.success("Observation ajoutée aux choix.");
            })
            .catch(() => toast.error("Erreur lors de l'ajout de l'observation."));
    };

    const reglementContrat = (cont) => {
        const target = cont || selectedContrat;
        executeReglementContrat(cont)
            .then((res) => {
                toast.success("Règlement effectué avec succès !");
                setModalModePaiement(false);
                // On N'ANNULE PLUS le reload : on ouvre d'abord le reçu (imprimable), la liste est
                // rafraîchie à la fermeture du reçu.
                ouvrirRecuApresEncaissement(target, res?.data);
            })
            .catch(() => toast.error("Erreur lors du règlement"));
    };

    const reglementsContrats = (contrats) => {
        Promise.all(contrats.map((c) => executeReglementContrat(c)))
            .then(() => { toast.success("Règlements effectués avec succès !"); window.location.reload(); })
            .catch(() => toast.error("Erreur lors du règlement"));
    };

    const handleDateChange = (_, dateStrings) => {
        const [s, e] = dateStrings;
        if (!s || !e) { setSelectedDate(null); return; }
        const toISO = (d) => { const [day, month, year] = d.split("/"); return `${year}-${month}-${day}`; };
        setSelectedDate([toISO(s), toISO(e)]);
        setCurrentPage(1);
    };

    const filteringOnCompanies = (companies) => {
        setSelectedCompagnies(companies);
        fetchData({
            pagination: { current: 1, pageSize },
            filters: { ...filteredInfo, compagnie: companies },
        });
    };

    const onSelectChange = (keys, rows) => {
        setSelectedRowKeys(keys);
        setSelectedRows(rows);
    };

    const rowSelection = { selectedRowKeys, onChange: onSelectChange };

    const isPoolSiege =
        LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" &&
        (LOGGED_USER?.agence?.typeAgence === "SIEGE" || LOGGED_USER?.agence === null);

    /* ── Cartes stat ── */
    const statCards = [
        {
            label: "Total en attente",
            value: total,
            sub: "contrats",
            color: "#fa8c16",
            bg: "#fff7e6",
            icon: "bx bx-time-five",
        },
        {
            label: "Contrats actifs",
            value: count?.valideCount ?? 0,
            sub: "contrats",
            color: "#52c41a",
            bg: "#f6ffed",
            icon: "bx bx-check-circle",
        },
        {
            label: "Contrats annulés",
            value: count?.annuleCount ?? 0,
            sub: "contrats",
            color: "#ff4d4f",
            bg: "#fff1f0",
            icon: "bx bx-x-circle",
        },
        {
            label: "En proposition",
            value: count?.enPropositionCount ?? 0,
            sub: "contrats",
            color: "#1890ff",
            bg: "#e6f7ff",
            icon: "bx bx-file",
        },
    ];

    const pendingCount  = filteredData.filter((r) => r.regle !== "REGLE").length;
    const settledCount  = filteredData.filter((r) => r.regle === "REGLE").length;

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"CAISSE"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* ── Titre ── */}
                            <div style={{ marginBottom: 24 }}>
                                <Title level={4} style={{ margin: 0, color: "#242166" }}>
                                    <ClockCircleOutlined style={{ marginRight: 8 }} />
                                    {v2 ? "Primes à encaisser" : "En attente d'encaissement"}
                                </Title>
                                <Text type="secondary">
                                    {v2
                                        ? "Règlements non effectués — affaires nouvelles et avenants"
                                        : "Contrats dont le règlement n'a pas encore été encaissé"}
                                </Text>
                            </div>

                            {/* ── Cartes statistiques ── */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                {statCards.map((s, i) => (
                                    <Col xs={24} sm={12} xl={6} key={i}>
                                        <Card
                                            style={{
                                                borderRadius: 12,
                                                border: `1px solid ${s.color}30`,
                                                background: s.bg,
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                            }}
                                            bodyStyle={{ padding: "16px 20px" }}
                                        >
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                <div>
                                                    <Text style={{ color: "#666", fontSize: 12, display: "block", marginBottom: 4 }}>
                                                        {s.label}
                                                    </Text>
                                                    <Text strong style={{ fontSize: 22, color: s.color, display: "block" }}>
                                                        {s.value}
                                                    </Text>
                                                    <Text style={{ fontSize: 12, color: s.color }}>{s.sub}</Text>
                                                </div>
                                                <div style={{
                                                    width: 44, height: 44, borderRadius: 10,
                                                    background: `${s.color}20`,
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                }}>
                                                    <i className={s.icon} style={{ fontSize: 22, color: s.color }} />
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            {/* ── Tableau ── */}
                            <Card
                                style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                                bodyStyle={{ padding: "16px 20px" }}
                            >
                                {/* Barre d'outils */}
                                <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
                                    <Col xs={24} sm={7}>
                                        <Input
                                            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                                            placeholder="Rechercher souscripteur, police..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            allowClear
                                            style={{ borderRadius: 8 }}
                                        />
                                    </Col>
                                    <Col xs={24} sm={7}>
                                        <ConfigProvider locale={frFR}>
                                            <RangePicker
                                                format="DD/MM/YYYY"
                                                onChange={handleDateChange}
                                                placeholder={["Date début", "Date fin"]}
                                                style={{ width: "100%", borderRadius: 8 }}
                                            />
                                        </ConfigProvider>
                                    </Col>
                                    {isPoolSiege && (
                                        <Col xs={24} sm={5}>
                                            <Select
                                                mode="multiple"
                                                placeholder="Filtre compagnie"
                                                onChange={filteringOnCompanies}
                                                options={membres.map((m) => ({ label: m.text, value: m.value, key: m.value }))}
                                                style={{ width: "100%", borderRadius: 8 }}
                                                maxTagCount={1}
                                            />
                                        </Col>
                                    )}
                                    <Col xs={24} sm={isPoolSiege ? 5 : 10} style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                                        <Tooltip title="Activer la sélection multiple pour encaissement groupé">
                                            <Space>
                                                <Text style={{ fontSize: 12, color: "#666" }}>Sélection</Text>
                                                <Switch
                                                    size="small"
                                                    checked={selecting}
                                                    onChange={setSelecting}
                                                />
                                            </Space>
                                        </Tooltip>
                                        <Button
                                            icon={<FileExcelOutlined />}
                                            onClick={exportToExcel}
                                            style={{
                                                borderRadius: 8,
                                                background: "#217346",
                                                borderColor: "#217346",
                                                color: "#fff",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Excel
                                        </Button>
                                    </Col>
                                </Row>

                                {/* Indicateurs rapides */}
                                <Row gutter={8} style={{ marginBottom: 12 }}>
                                    <Col>
                                        <Tag color="warning" icon={<ClockCircleOutlined />}>
                                            {pendingCount} non réglé{pendingCount > 1 ? "s" : ""}
                                        </Tag>
                                    </Col>
                                    <Col>
                                        <Tag color="success" icon={<CheckCircleOutlined />}>
                                            {settledCount} réglé{settledCount > 1 ? "s" : ""}
                                        </Tag>
                                    </Col>
                                    {selecting && selectedRows.length > 0 && (
                                        <Col>
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<CreditCardOutlined />}
                                                onClick={() => {
                                                    const nonRegle = selectedRows.filter((r) => r.regle !== "REGLE");
                                                    if (nonRegle.length === 0) {
                                                        toast.info("Tous les contrats sélectionnés sont déjà réglés");
                                                        return;
                                                    }
                                                    setContractsToPay(nonRegle);
                                                    setModalModePaiement(true);
                                                }}
                                                style={{ borderRadius: 6, background: "#52c41a", borderColor: "#52c41a" }}
                                            >
                                                Encaisser la sélection ({selectedRows.filter((r) => r.regle !== "REGLE").length})
                                            </Button>
                                        </Col>
                                    )}
                                </Row>

                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingContrats}>
                                        <Table
                                            rowSelection={selecting ? rowSelection : null}
                                            columns={columns}
                                            rowKey="key"
                                            dataSource={filteredData}
                                            onChange={(pag, filters, sorter) => {
                                                setCurrentPage(pag.current);
                                                setPageSize(pag.pageSize);
                                                setPagination(pag);
                                                setFilteredInfo(filters);
                                                setSortedInfo(sorter);
                                            }}
                                            pagination={{
                                                current: currentPage,
                                                pageSize,
                                                total,
                                                showSizeChanger: true,
                                                showTotal: (t) => `${t} contrats`,
                                                pageSizeOptions: ["10", "20", "50"],
                                            }}
                                            onRow={(record) => ({
                                                style: {
                                                    background: record.regle === "REGLE"
                                                        ? STATUS_COLORS.regle.bg
                                                        : STATUS_COLORS.nonRegle.bg,
                                                    borderLeft: `3px solid ${record.regle === "REGLE"
                                                        ? STATUS_COLORS.regle.border
                                                        : STATUS_COLORS.nonRegle.border}`,
                                                    transition: "all 0.2s",
                                                },
                                            })}
                                            size="middle"
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </Card>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade" />
                    </div>
                </div>
            </div>

            {/* ── Modal mode de paiement ── */}
            <Modal
                title={
                    <Space>
                        <CreditCardOutlined />
                        <span>Mode de paiement</span>
                    </Space>
                }
                centered
                open={modalModePaiement}
                onOk={() =>
                    selecting && contractsToPay.length > 0
                        ? alertReglements(contractsToPay)
                        : alertReglement(selectedContrat)
                }
                onCancel={() => setModalModePaiement(false)}
                okText="Encaisser"
                cancelText="Annuler"
                okButtonProps={{ style: { background: "#52c41a", borderColor: "#52c41a" } }}
            >
                <Form form={modePaiementForm} layout="vertical">
                    <Form.Item
                        name="modePaiement"
                        label="Mode de paiement"
                        rules={[{ required: true, message: "Veuillez sélectionner un mode" }]}
                    >
                        <Select
                            onChange={(v) => setModePaiement(v)}
                            placeholder="Sélectionner le mode de paiement"
                            style={{ borderRadius: 8 }}
                        >
                            <Option value="ESPECES">Espèces</Option>
                            <Option value="BANQUE">Banque (Chèque)</Option>
                            <Option value="VIREMENT">Banque (Virement)</Option>
                            <Option value="MOBILE_MONEY">Mixx By Yas / Moov Money</Option>
                        </Select>
                    </Form.Item>
                    {/* Encaissement PARTIEL (HLA093) — uniquement pour un contrat seul (pas le groupé). */}
                    {canEncaissementPartiel && !(selecting && contractsToPay.length > 0) && selectedContrat && (
                        <Form.Item
                            label="Montant encaissé"
                            help={
                                montantEncaisse != null &&
                                selectedContrat.rawPrimeTotale != null &&
                                montantEncaisse < selectedContrat.rawPrimeTotale
                                    ? `Encaissement partiel — reste à payer : ${displayNumbers(
                                          selectedContrat.rawPrimeTotale - montantEncaisse
                                      )} FCFA`
                                    : "Prime totale (encaissement plein). Diminuez le montant pour un encaissement partiel."
                            }
                        >
                            <InputNumber
                                value={montantEncaisse}
                                onChange={(v) => setMontantEncaisse(v)}
                                min={0}
                                max={selectedContrat.rawPrimeTotale}
                                style={{ width: "100%", borderRadius: 8 }}
                                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                parser={(v) => v.replace(/\s/g, "")}
                                addonAfter="FCFA"
                            />
                        </Form.Item>
                    )}
                    {(modePaiement === "BANQUE" || modePaiement === "MOBILE_MONEY") && (
                        <Form.Item
                            name="numeroPaiement"
                            label={modePaiement === "BANQUE" ? "Numéro de chèque" : "Numéro Flooz / TMoney"}
                            rules={[{ required: true, message: "Veuillez renseigner ce champ" }]}
                        >
                            <Input
                                placeholder={modePaiement === "BANQUE" ? "Numéro du chèque" : "Numéro de téléphone"}
                                onChange={(e) => setNumeroPaiement(e.target.value)}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    )}
                    {(modePaiement === "BANQUE" || modePaiement === "VIREMENT") && (
                        <Form.Item
                            name="banque"
                            label="Banque"
                            rules={[{ required: true, message: "Veuillez sélectionner la banque" }]}
                        >
                            <Select
                                onChange={(v) => setBanque(v)}
                                placeholder="Sélectionner la banque"
                                style={{ borderRadius: 8 }}
                            >
                                {banques.map((b) => (
                                    <Option key={b.parametreId} value={b.libelleParametre}>
                                        {b.libelleParametre}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    {(modePaiement === "MOBILE_MONEY" || modePaiement === "VIREMENT") && (
                        <Form.Item
                            name="referencePaiement"
                            label="Référence du paiement"
                            rules={[{ required: true, message: "Veuillez renseigner la référence" }]}
                        >
                            <Input
                                placeholder="Numéro de référence"
                                onChange={(e) => setReference(e.target.value)}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    )}
                    {modePaiement === "VIREMENT" && (
                        <Form.Item
                            name="dateVirement"
                            label="Date du virement"
                            rules={[{ required: true, message: "Veuillez sélectionner la date" }]}
                        >
                            <ConfigProvider locale={frFR}>
                                <DatePicker
                                    placeholder="Date du virement"
                                    onChange={(_, v) => setDateVirement(v)}
                                    style={{ width: "100%", borderRadius: 8 }}
                                    disabledDate={(c) => c && c > dayjs().endOf("day")}
                                />
                            </ConfigProvider>
                        </Form.Item>
                    )}
                    <Divider style={{ margin: "8px 0" }} />
                    <Form.Item label="Observation / commentaire (facultatif)">
                        <Select
                            allowClear
                            showSearch
                            value={observationSelected}
                            placeholder="Choisir une observation"
                            optionFilterProp="children"
                            style={{ borderRadius: 8, marginBottom: 8 }}
                            onChange={(v) => { setObservationSelected(v); setObservation(v || ""); }}
                            dropdownRender={(menu) => (
                                <>
                                    {menu}
                                    <Divider style={{ margin: "6px 0" }} />
                                    <div style={{ display: "flex", gap: 8, padding: "0 8px 8px" }}>
                                        <Input
                                            placeholder="Nouveau choix"
                                            value={newObservation}
                                            onChange={(e) => setNewObservation(e.target.value)}
                                            onKeyDown={(e) => e.stopPropagation()}
                                            onPressEnter={addObservationChoice}
                                        />
                                        <Button type="primary" onClick={addObservationChoice}>Ajouter</Button>
                                    </div>
                                </>
                            )}
                        >
                            {observationChoices.map((o) => (
                                <Option key={o.parametreId || o.libelleParametre} value={o.libelleParametre}>
                                    {o.libelleParametre}
                                </Option>
                            ))}
                        </Select>
                        <Input.TextArea
                            rows={2}
                            value={observation}
                            placeholder="Commentaire libre (modifiable)"
                            onChange={(e) => setObservation(e.target.value)}
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* ── Reçu d'encaissement, ouvert AUTOMATIQUEMENT après un encaissement réussi ── */}
            <Modal
                title="Reçu d'encaissement"
                open={recuOpen}
                onCancel={fermerRecuEtRafraichir}
                width={820}
                maskClosable={false}
                footer={[
                    <Button key="close" onClick={fermerRecuEtRafraichir}>Fermer</Button>,
                    <Button
                        key="print"
                        type="primary"
                        icon={<CreditCardOutlined />}
                        onClick={handleRecu}
                        disabled={loadingRecu || !recuReglement}
                    >
                        Imprimer le reçu
                    </Button>,
                ]}
            >
                {loadingRecu ? (
                    <div style={{ textAlign: "center", padding: 24 }}>
                        <Spin tip="Chargement du reçu…" />
                    </div>
                ) : (
                    <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                        <RecuCurrentFormat ref={printRecu} contrat={recuContrat} reglement={recuReglement} />
                    </div>
                )}
            </Modal>

            <div className="layout-overlay layout-menu-toggle" />
            <div className="drag-target" />
        </div>
    );
};

export default EnAttenteDEncaissementV2;
